import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, CheckCircle2, XCircle, MessageCircle, ArrowRight, Clock, AlertTriangle } from "lucide-react";
import { endpoints } from "../lib/api";
import { waLink, openWhatsApp, templates } from "../lib/wa";
import { ConversionLikelihoodBadge } from "../components/ScoringBadges";
import { EmptyState } from "../components/EmptyState";
import { toast } from "sonner";

const SECTION_META = {
  today:    { Icon: Clock,          tone: "trials", title: "Today",            empty: "No trials scheduled today." },
  upcoming: { Icon: Calendar,       tone: "ops",    title: "Upcoming",         empty: "Nothing upcoming on the trial calendar." },
  missed:   { Icon: AlertTriangle,  tone: "risk",   title: "Missed / No-show", empty: "Nothing missed. Strong show-up rate." },
  done:     { Icon: CheckCircle2,   tone: "growth", title: "Completed",        empty: "No completed trials yet. Your first conversion starts here." },
};

const TONE_ICON = {
  growth: "text-growth bg-growth/10",
  leads:  "text-leads  bg-leads/10",
  trials: "text-trials bg-trials/10",
  risk:   "text-risk   bg-risk/10",
  ops:    "text-muted-foreground bg-muted",
};

export default function Trials() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await endpoints.listLeads();
      setLeads(data.filter(l => l.trial_date));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const buckets = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayEnd = new Date(todayStart); todayEnd.setDate(todayEnd.getDate() + 1);
    const today = [], upcoming = [], missed = [], done = [];
    leads.forEach(l => {
      const d = new Date(l.trial_date);
      if (l.trial_status === "attended") done.push(l);
      else if (l.trial_status === "no_show") missed.push(l);
      else if (d >= todayStart && d < todayEnd) today.push(l);
      else if (d >= todayEnd) upcoming.push(l);
      else missed.push(l); // past & still pending = missed
    });
    return { today, upcoming, missed, done };
  }, [leads]);

  const mark = async (id, trial_status) => {
    const updates = { trial_status };
    if (trial_status === "attended") updates.status = "trial_done";
    if (trial_status === "no_show") updates.status = "lost";
    await endpoints.updateLead(id, updates);
    toast.success(`Marked ${trial_status.replace("_", " ")}`);
    load();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-5xl mx-auto">
      <header className="mb-8 lg:mb-10">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-trials mb-2">Pipeline</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-foreground flex items-center gap-2.5" data-testid="trials-title">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-trials/10 text-trials">
            <Calendar className="w-5 h-5" strokeWidth={2.4} />
          </span>
          Trials
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-md">Confirm today, follow up on no-shows, convert what attended.</p>
      </header>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading trials...</div>
      ) : (
        <div className="space-y-8">
          <Section sectionKey="today"    items={buckets.today}    onMark={mark} testid="bucket-today" />
          <Section sectionKey="upcoming" items={buckets.upcoming} onMark={mark} testid="bucket-upcoming" />
          <Section sectionKey="missed"   items={buckets.missed}   onMark={mark} testid="bucket-missed" />
          <Section sectionKey="done"     items={buckets.done}     onMark={mark} testid="bucket-done" />
        </div>
      )}
    </div>
  );
}

function Section({ sectionKey, items, onMark, testid }) {
  const meta = SECTION_META[sectionKey];
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2.5 mb-3.5">
        <span className={`w-7 h-7 rounded-md flex items-center justify-center ${TONE_ICON[meta.tone]}`}>
          <meta.Icon className="w-4 h-4" strokeWidth={2.4} />
        </span>
        <h2 className="font-display text-lg font-bold text-foreground tracking-tight">{meta.title}</h2>
        <span className="text-[11px] font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border/70 py-10 text-center">
          <p className="text-sm text-muted-foreground">{meta.empty}</p>
        </div>
      ) : (
        <div className="border border-border rounded-2xl divide-y divide-border/60 bg-card overflow-hidden">
          {items.map(l => (
            <div key={l.id} className="flex items-center gap-4 p-4 hover:bg-foreground/[0.02] transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-foreground truncate">{l.name}</span>
                  <ConversionLikelihoodBadge lead={l} size="sm" />
                </div>
                <div className="text-xs text-muted-foreground truncate mt-0.5">
                  {new Date(l.trial_date).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  {" · "}{l.source || "Walk-in"}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {l.trial_status === "pending" && (
                  <>
                    <button
                      onClick={() => onMark(l.id, "attended")}
                      data-testid={`attended-${l.id}`}
                      className="inline-flex items-center gap-1 text-growth bg-growth/10 hover:bg-growth/20 text-xs font-semibold px-2.5 py-1.5 rounded-md transition-colors"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Attended
                    </button>
                    <button
                      onClick={() => onMark(l.id, "no_show")}
                      data-testid={`noshow-${l.id}`}
                      className="inline-flex items-center gap-1 text-risk bg-risk/10 hover:bg-risk/20 text-xs font-semibold px-2.5 py-1.5 rounded-md transition-colors"
                    >
                      <XCircle className="w-3.5 h-3.5" /> No-show
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => openWhatsApp(l.phone, l.trial_status === "attended" ? templates.trial_feedback(l.name) : templates.trial_reminder(l.name, new Date(l.trial_date).toLocaleDateString()))}
                  className="inline-flex items-center gap-1 bg-[#25D366] hover:bg-[#1EBE5D] text-white text-xs font-medium px-2.5 py-1.5 rounded-md transition-colors"
                  aria-label="WhatsApp"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                </button>
                <Link to={`/leads/${l.id}`} className="p-1.5 text-muted-foreground/60 hover:text-foreground hover:bg-muted rounded-md transition-colors">
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
